import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import { SUBORDINATE_LEVELS } from '../../../constants';

/**
 * Add/Edit Unit Modal Component
 *
 * @param {Boolean} open - Whether the modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {Function} onConfirm - Callback when unit is confirmed with unit data
 * @param {Array} unitLevels - List of available unit levels
 * @param {Array} verifyModes - List of available verify modes for multi-select
 * @param {Object} unitData - Unit data for editing (null for adding)
 * @param {Boolean} editMode - Whether in edit mode
 */
function AddUnitModal({ open, onClose, onConfirm, unitLevels = [], verifyModes = [], unitData = null, editMode = false }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    level: '',
    location: '',
    subordinateLevelId: '',
    verifyModeIds: [],
  });
  const [errors, setErrors] = useState({});

  // Get subordinate levels with translated names
  const subordinateLevelOptions = SUBORDINATE_LEVELS.map(level => ({
    id: level.id,
    name: t(level.nameKey),
  }));

  // Populate form data when in edit mode
  useEffect(() => {
    if (editMode && unitData) {
      setFormData({
        code: unitData.code || '',
        name: unitData.name || '',
        level: unitData.level !== undefined ? unitData.level : '',
        location: unitData.location || '',
        subordinateLevelId: unitData.subordinateLevelId !== undefined ? unitData.subordinateLevelId : '',
        verifyModeIds: unitData.verifyModeIds || [],
      });
    } else {
      setFormData({
        code: '',
        name: '',
        level: '',
        location: '',
        subordinateLevelId: '',
        verifyModeIds: [],
      });
    }
    setErrors({});
  }, [editMode, unitData, open]);

  // Handle input change
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });

    // No validation needed
    setErrors({});
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    return formData.code && formData.name && formData.level !== '';
  };

  // Validate form
  const validateForm = () => {
    // No validation needed
    return true;
  };

  // Handle confirm
  const handleConfirm = () => {
    if (!validateForm()) {
      return;
    }

    // Clean up data before submitting - convert empty strings to null for nullable fields
    const cleanedData = {
      ...formData,
      // Convert empty string subordinateLevelId to null
      subordinateLevelId: formData.subordinateLevelId === '' || formData.subordinateLevelId === null ? null : parseInt(formData.subordinateLevelId, 10),
      // Ensure level is a number
      level: typeof formData.level === 'string' ? parseInt(formData.level, 10) : formData.level,
      // Ensure verifyModeIds is an array
      verifyModeIds: Array.isArray(formData.verifyModeIds) ? formData.verifyModeIds : [],
    };

    if (editMode && unitData) {
      // Include the unit ID and version for update (version needed for concurrency check)
      onConfirm({ ...cleanedData, id: unitData.id, version: unitData.version });
    } else {
      onConfirm(cleanedData);
    }
    handleClose();
  };

  // Handle close and reset form
  const handleClose = () => {
    setFormData({
      code: '',
      name: '',
      level: '',
      location: '',
      subordinateLevelId: '',
      verifyModeIds: [],
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
        }}
      >
        {editMode ? <EditIcon /> : <AddIcon />}
        {editMode ? t('units.editDialog.title') : t('units.addDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 2 }}>
          <Input
            label={t('units.addDialog.code')}
            value={formData.code}
            onChange={handleChange('code')}
            required
            autoFocus
            disabled={editMode}
            id="code"
            name="code"
            data-testid="unit-modal-input-code"
          />
          <Input
            label={t('units.addDialog.name')}
            value={formData.name}
            onChange={handleChange('name')}
            required
            id="name"
            name="name"
            data-testid="unit-modal-input-name"
          />
          <Select
            label={t('units.addDialog.level')}
            value={formData.level}
            onChange={handleChange('level')}
            options={unitLevels}
            required
            emptyMessage={t('units.addDialog.noLevels')}
            id="level"
            name="level"
            data-testid="unit-modal-select-level"
          />
          <Input
            label={t('units.addDialog.location')}
            value={formData.location}
            onChange={handleChange('location')}
            id="location"
            name="location"
            data-testid="unit-modal-input-location"
          />
          <Select
            label={t('units.addDialog.parentUnit')}
            value={formData.subordinateLevelId}
            onChange={handleChange('subordinateLevelId')}
            options={subordinateLevelOptions}
            emptyMessage={t('units.addDialog.noParentUnits')}
            id="subordinateLevelId"
            name="subordinateLevelId"
            data-testid="unit-modal-select-subordinateLevelId"
          />
          <Autocomplete
            multiple
            id="verifyModeIds"
            size="small"
            options={verifyModes}
            getOptionLabel={(option) => option.name || ''}
            value={verifyModes.filter((vm) => formData.verifyModeIds.includes(vm.id))}
            onChange={(event, newValue) => {
              setFormData({
                ...formData,
                verifyModeIds: newValue.map((vm) => vm.id),
              });
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('units.addDialog.verifyModes')}
                placeholder={formData.verifyModeIds.length === 0 ? t('units.addDialog.selectVerifyModes') : ''}
                variant="outlined"
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  ...params.inputProps,
                  id: 'verifyModeIds-input',
                  name: 'verifyModeIds',
                  'data-testid': 'unit-modal-autocomplete-verifyModeIds',
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  {...getTagProps({ index })}
                  key={option.id}
                  size="small"
                />
              ))
            }
            noOptionsText={t('units.addDialog.noVerifyModes')}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          id="cancel"
          name="cancel"
          data-testid="unit-modal-button-cancel"
        >
          {t('units.addDialog.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!isFormValid() || Object.values(errors).some(error => error !== '')}
          startIcon={editMode ? <EditIcon /> : <AddIcon />}
          id="confirm"
          name="confirm"
          data-testid="unit-modal-button-confirm"
        >
          {editMode ? t('units.editDialog.confirm') : t('units.addDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddUnitModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  unitLevels: PropTypes.array,
  verifyModes: PropTypes.array,
  unitData: PropTypes.object,
  editMode: PropTypes.bool,
};

export default AddUnitModal;
